// eslint-disable-next-line no-restricted-imports -- type-only; runtime dayjs comes from dateUtils
import type { Dayjs } from 'dayjs';


// Один момент времени в формате dayjs.
export type DateRangeDayjs = Dayjs;

// Полный валидный диапазон: начало и конец.
export type DayjsRange = [DateRangeDayjs, DateRangeDayjs];
// Конец диапазона, который пользователь изменял последним.
export type DayjsRangeEnd = 'start' | 'end';
// Промежуточное состояние выбора: даты могут отсутствовать, пока диапазон не завершён.
export type DayjsRangeNullable = [DateRangeDayjs | null, DateRangeDayjs | null] | null;
// Промежуточное значение календаря Ant Design: массив может быть неполным.
export type CalendarChangeRange = (DateRangeDayjs | null)[] | null;


export const isCompleteDayjsRange = (
	range: DayjsRangeNullable | undefined,
): range is DayjsRange => {
	return range != null
		&& range.length === 2
		&& range[0] != null
		&& range[1] != null;
};


export const getDayjsRangeSpanDays = (range: DayjsRange): number => {
	return range[1].diff(range[0], 'day');
};


export const clampDayjsRangeToMaxSpan = (
	range: DayjsRangeNullable | undefined,
	maxSpanDays: number,
	changedEnd: DayjsRangeEnd | null = null,
): DayjsRangeNullable => {
	if (!isCompleteDayjsRange(range)) {
		return range ?? null;
	}
	const [start, end] = range;
	const spanDays = getDayjsRangeSpanDays([start, end]);
	if (spanDays <= maxSpanDays) {
		return [start, end];
	}
	if (changedEnd === 'end') {
		return [end.subtract(maxSpanDays, 'day'), end];
	}
	if (changedEnd === 'start') {
		return [start, start.add(maxSpanDays, 'day')];
	}
	return [start, start.add(maxSpanDays, 'day')];
};


export const createMaxSpanDisabledDate = (
	maxSpanDays: number,
	getCalendarRange: () => CalendarChangeRange,
) => {
	return (current: DateRangeDayjs) => {
		const calendarRange = getCalendarRange();
		if (calendarRange == null || calendarRange.length === 0) {
			return false;
		}
		const start = calendarRange[0];
		const end = calendarRange[1];
		const tooLate = start != null && current.diff(start, 'day') > maxSpanDays;
		const tooEarly = end != null && end.diff(current, 'day') > maxSpanDays;
		return Boolean(tooLate || tooEarly);
	};
};


export const isSameDayjsRange = (
	left: DayjsRangeNullable | undefined,
	right: DayjsRangeNullable | undefined,
): boolean => {
	if (left == null && right == null) {
		return true;
	}
	if (!isCompleteDayjsRange(left) || !isCompleteDayjsRange(right)) {
		return false;
	}
	return left[0].valueOf() === right[0].valueOf()
		&& left[1].valueOf() === right[1].valueOf();
};
